import {
	PublicKey,
	VelocityClient,
	getUserStatsAccountPublicKey,
} from '@velocity-exchange/sdk';

/**
 * Next sub-account id for a fresh account initialization: the authority's
 * `userStats.numberOfSubAccountsCreated`, or 0 when the authority has no stats
 * account yet (a genuinely new user).
 *
 * Returns `null` when the id is *unknown* — the fetch failed. A missing stats
 * account and a failed lookup are indistinguishable from a thrown error, and
 * treating the latter as 0 signs an initialization for a sub-account that
 * already exists. Callers that can still abort cheaply should; callers already
 * past the point of no return may fall back to 0.
 */
export const getUserIdForAccountInitialization = async (
	velocityClient: VelocityClient,
	authority: PublicKey
): Promise<number | null> => {
	try {
		const userStatsAccountKey = getUserStatsAccountPublicKey(
			velocityClient.program.programId,
			authority
		);

		// fetchNullable, not fetch: a missing stats account is the normal
		// new-user case, and must not surface as the same error an RPC failure
		// would. Velocity migration: stricter VelocityProgram typing triggers
		// TS2589.
		const dataAndContext = await (
			velocityClient.program as any
		).account.userStats.fetchNullableAndContext(
			userStatsAccountKey,
			'finalized'
		);

		if (!dataAndContext?.data) {
			return 0;
		}

		return dataAndContext.data.numberOfSubAccountsCreated as number;
	} catch (e: any) {
		console.error(
			`Failed fetching user stats account for authority ${authority.toString()}`,
			e
		);
		return null;
	}
};
